import React, {createContext, useState} from 'react';

import {AudioSpeed} from '@ael/shared-components-library';

interface AudioElementContextProvided {
  isAutoPlayOn: boolean;
  isMuted: boolean;
  toggleMute: () => void;
  audioSpeed: AudioSpeed;
  changeSpeed: (speed: AudioSpeed) => void;
}

const AudioElementContext = createContext<AudioElementContextProvided | null>(null);

type AudioElementContextProviderProps = {
  children?: React.ReactNode;
  isAutoPlayOn: boolean;
};

/**
 * Provides user's global preferences regarding audio.
 */
export const AudioElementContextProvider = (props: AudioElementContextProviderProps) => {
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [audioSpeed, setAudioSpeed] = useState<AudioSpeed>(1);
  const toggleMute = () => setIsMuted((prev) => !prev);
  const changeSpeed = (speed: AudioSpeed) => setAudioSpeed(speed);

  const provided = {
    isAutoPlayOn: props.isAutoPlayOn,
    isMuted,
    audioSpeed,
    toggleMute,
    changeSpeed,
  };

  return <AudioElementContext.Provider value={provided}>{props.children}</AudioElementContext.Provider>;
};

/**
 * Returns context to control an Audio HTML element.
 * This element and context exists to support audio autoplay in browser due to browsers restrictive actions against
 * audio autoplay.
 */
export const useAudioElementContext = () => {
  const context = React.useContext(AudioElementContext);
  if (context) {
    return context;
  }
  throw Error('Accessing Audio Element context outside of Audio Element Provider.');
};
